import { Formik, Form } from 'formik'
import {
  InputField,
  InputGroupField,
  LoaderButton,
  ImageDropzone
} from 'components/molecules'
import cmsHomepageSectionEditScheme from './cms-homepage-section-edit-scheme'
import PropTypes from 'prop-types'
import { Alert, Button, Col, FormGroup, Label, Row } from 'reactstrap'
import { useState } from 'react'

const propTypes = {
  section: PropTypes.shape({
    is_active           : PropTypes.bool,
    name                : PropTypes.string,
    order               : PropTypes.number,
    alt_text            : PropTypes.string,
    landscape_image_url : PropTypes.string,
    portrait_image_url  : PropTypes.string,
    header_text         : PropTypes.string,
    caption_text        : PropTypes.string,
    title_text          : PropTypes.string,
    button_text         : PropTypes.string,
    seo_title           : PropTypes.string,
    url                 : PropTypes.string,
    platform            : PropTypes.string
  }),
  handleSubmitForm: PropTypes.func
}


const defaultProps = {
  section: {
    is_active           : true,
    name                : '',
    order               : 1,
    alt_text            : '',
    landscape_image_url : '',
    portrait_image_url  : '',
    header_text         : '',
    caption_text        : '',
    platform            : '',
    title_text          : '',
    button_text         : '',
    seo_title           : '',
    url                 : ''
  },
  handleSubmitForm: () => {}
}

const URL_TYPE = {
  EXTERNAL : 'external',
  INTERNAL : 'internal'
}

const PLATFORM_CONST = {
  WEB    : 'web',
  MOBILE : 'mobile'
}

const CmsHomepageSectionEditForm = ({
  section,
  handleSubmitForm
}) => {

  // if internal url, it will start with '/' because it's still in https://www.tinkerlust.com domain
  // internal url ex: '/collections/shoes-disc-up-to-70' which will result 'https://www.tinkerlust.com//collections/shoes-disc-up-to-70'
  // external url ex: 'https://wa.me/628216387123'
  const defaultUrlType = section.url.charAt(0) === '/' ? URL_TYPE.INTERNAL : URL_TYPE.EXTERNAL

  const [ selectedUrlType, setSelectedUrlType ] = useState(defaultUrlType)

  const mobilePlatformValue = 'mobile'

  const handleDrop = (formik, fieldName, acceptedFiles) => {
    formik.setFieldValue(fieldName, acceptedFiles[0])
  }

  const handleClickRemove = (formik, fieldName) => {
    formik.setFieldValue(fieldName, '')
  }

  const handleClickInternalUrl = () => setSelectedUrlType(URL_TYPE.INTERNAL)

  const handleClickExternalUrl = () => setSelectedUrlType(URL_TYPE.EXTERNAL)

  const addonType = selectedUrlType === URL_TYPE.EXTERNAL ? '' : 'prepend'

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={{
        is_active           : section.is_active || false,
        name                : section.name || '',
        order               : section.order || '',
        alt_text            : section.alt_text || '',
        seo_title           : section.seo_title || '',
        header_text         : section.header_text || '',
        platform            : section.platform || '',
        title_text          : section.title_text || '',
        button_text         : section.button_text || '',
        caption_text        : section.caption_text || '',
        landscape_image_url : section.landscape_image_url || '',
        portrait_image_url  : section.portrait_image_url || '',
        url                 : section.url || '',
        landscape_image     : '',
        portrait_image      : ''
      }}
      validationSchema={ cmsHomepageSectionEditScheme }
      onSubmit={ handleSubmitForm }
    >
      {
        (formik) => (
          <Form>
            <Row>
              <Col xs={ 6 }>
                {
                  formik.values.platform === mobilePlatformValue &&
                  (
                    <Alert color="warning">
                      { 'Gambar untuk platform mobile, ' }
                      <span className='font-weight-bold'>{ 'TIDAK BOLEH' }</span>
                      { ' mengandung logo playstore dan appstore.' }
                    </Alert>
                  )
                }
                <FormGroup>
                  <Label className='font-weight-bolder'>{ 'Landscape Image' }</Label>
                  <ImageDropzone
                    imageUrl={ formik.values.landscape_image_url }
                    file={ formik.values.landscape_image }
                    placeholder={ 'Gambar untuk devide layar besar, seperti laptop/pc.' }
                    onDrop={ (acceptedFile) => handleDrop(formik, 'landscape_image', acceptedFile) }
                    onClickRemove={ () => handleClickRemove(formik, 'landscape_image') }
                  />
                  {
                    formik.errors.desktop_image && (
                      <div className='text-warning'>{ formik.errors.desktop_image }</div>
                    )
                  }
                </FormGroup>

                <FormGroup>
                  <Label className='font-weight-bolder'>{ 'Portrait Image' }</Label>
                  <ImageDropzone
                    imageUrl={ formik.values.portrait_image_url }
                    placeholder='Gambar untuk devide layar kecil, seperti mobile/tablet.'
                    file={ formik.values.portrait_image }
                    onDrop={ (acceptedFile) => handleDrop(formik, 'portrait_image', acceptedFile) }
                    onClickRemove={ () => handleClickRemove(formik, 'portrait_image') }
                  />
                  {
                    formik.errors.portrait_image && (
                      <div className='text-warning'>{ formik.errors.portrait_image }</div>
                    )
                  }
                </FormGroup>
                <InputField
                  name='alt_text'
                  label='Image Alt Text'
                  type='text'
                  required={ true }
                  formText='Deskripsikan gambar dengan kata kata untuk optimisasi SEO.'
                />
              </Col>
              <Col xs={ 6 }>
                <div className='d-flex justify-content-between'>
                  <InputField
                    formGroupClassName='mr-3 w-50'
                    name='name'
                    label='Name'
                    type='text'
                    disabled={ true }
                    required={ true }
                    formText={ 'Nama untuk Homepage Section. Digunakan untuk track posision (Segment) atau creative_slot (GA4)' }
                  />
                  <InputField
                    formGroupClassName='mr-3'
                    name='order'
                    label='Order'
                    type='number'
                    min={ 1 }
                    max={ 10 }
                    required={ true }
                  />
                  <InputField
                    type='select'
                    name='is_active'
                    label="Is Active?"
                    required={ true }
                  >
                    <option value={ true }> { 'YES' } </option>
                    <option value={ false }> { 'NO' } </option>
                  </InputField>
                </div>

                <InputField
                  type='select'
                  name='platform'
                  label="Platform"
                  required={ true }
                  formText={ (
                    <>
                      <div>
                        <span className="font-weight-bold mr-1">{ 'web' }</span>
                        <span>{ ' : ditampilkan pada browser (www.tinkerlust.com).' }</span>
                      </div>
                      <div>
                        <span className="font-weight-bold mr-1">{ 'mobile' }</span>
                        <span>{ ' : ditampilkan pada mobile app (aplikasi Tinkerlust pada Android/IOS).' }</span>
                      </div>
                    </>
                  ) }
                >
                  {
                    Object.keys(PLATFORM_CONST).map((key) => (
                      <option
                        key={ `platform-${key}` }
                        value={ PLATFORM_CONST[ key ] }
                      >
                        { PLATFORM_CONST[ key ] }
                      </option>
                    ))
                  }

                </InputField>

                <InputField
                  required
                  name='title_text'
                  label='Campaign Name (Title Text)'
                  type='text'
                  formText='Nama Kampanye atau Promosi. Digunakan untuk identify the campaign/promotion di analytics.'
                />

                <InputField
                  name='caption_text'
                  label='Campaign Description (Caption Text)'
                  type='textarea'
                  rows={ 2 }
                  placeholder='Shop now for great discounts...'
                  formText='Deskripsi singkat untuk Kampanye atau Promosi. Akan ditampilkan dibawah nama kampanye/promosi. Gunakan "Enter" untuk menambahkan baris'
                />

                <InputField
                  name='button_text'
                  label='Button Text'
                  type='input'
                  placeholder='Shop now...'
                  formText="Text untuk Custom button, pastikan sesuai dengan konten promo/kampanye. Jika keterangan ini kosong, akan otomatis terisi dengan 'Shop Now'"
                />

                <div className='float-right'>
                  <Button
                    outline
                    onClick={ handleClickInternalUrl }
                    size='sm'
                    color='primary'
                    disabled={ selectedUrlType === URL_TYPE.INTERNAL }
                  >
                    { 'Internal Url' }
                  </Button>

                  <Button
                    outline
                    onClick={ handleClickExternalUrl }
                    size='sm'
                    color='primary'
                    disabled={ selectedUrlType === URL_TYPE.EXTERNAL }
                  >
                    { 'External Url' }
                  </Button>
                </div>

                <InputGroupField
                  name='url'
                  label='Url'
                  type='text'
                  inputGroupText='https://www.tinkerlust.com'
                  addonType={ addonType }
                  formText="Halaman untuk me-redirect User ketika mengklik gambar kampanye. Pilih `External URL` apabila alamat url bukan dari tinkerlust.com"
                >
                  <Button
                    outline
                    tag={ 'a' }
                    target={ '_blank' }
                    href={ formik.values.url }
                    size='sm'
                    className='float-right'
                    color='primary'
                  >
                    { 'Open the Url Link' }
                  </Button>
                </InputGroupField>

                <InputField
                  name='seo_title'
                  label='SEO Title'
                  type='text'
                  formText='Used to override the page of the title purposes. Not applicable for Banners.'
                />
                <InputField
                  name='header_text'
                  label='Header Text'
                  type='text'
                  formText='Title of this section on the page. Leave blank.'
                />

                <LoaderButton
                  className='w-100'
                  isLoading={ formik.isSubmitting }
                  onClick={ formik.submitForm }
                  block={ false }
                  color='primary'
                  text="UPDATE"
                />
              </Col>
            </Row>


          </Form>
        )
      }
    </Formik>
  )
}
CmsHomepageSectionEditForm.propTypes    = propTypes
CmsHomepageSectionEditForm.defaultProps = defaultProps

export default CmsHomepageSectionEditForm
